import React, { useState, useEffect } from 'react';
import { formatEighthInches } from '../lib/measurableDisplay';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminPage, { usesRail } from '../components/AdminShell';
import '../components/lists/Lists.css';
import './CompareAthletesPage.css';
import axios from 'axios';
import VerifiedFlame from '../components/VerifiedFlame';
import { PageHeader, MonoLabel, EditorialButton } from '../components/Editorial';
import Button from '../components/Button';

// year_offset is canonical: 0=Freshman … 3=Senior (matches iOS, the source of truth).
const YEAR_LABELS = ['Freshman', 'Sophomore', 'Junior', 'Senior'];

function formatHeight(inches) {
  if (!inches) return '—';
  const total = Math.round(inches); // avoid float-modulo artifacts on fractional heights
  return `${Math.floor(total / 12)}'${total % 12}"`;
}

const INFO_ROWS = [
  { label: 'School', key: 'high_school' },
  { label: 'Location', render: a => [a.city, a.state].filter(Boolean).join(', ') || '—' },
  { label: 'Class', render: a => a.graduation_year ? `${a.graduation_year}` : '—' },
  { label: 'Position(s)', render: a => [a.position_primary, a.position_secondary, a.position_other].filter(Boolean).join(', ') || '—' },
];

const MEASURABLE_ROWS = [
  { label: 'Height', render: a => formatHeight(a.height_inches), vKey: 'height_inches_verified' },
  { label: 'Weight', render: a => a.weight_lbs ? `${a.weight_lbs} lbs` : '—', vKey: 'weight_lbs_verified' },
  { label: 'Wingspan', render: a => formatHeight(a.wingspan), vKey: 'wingspan_verified' },
  { label: 'Hand Size', render: a => formatEighthInches(a.hand_size), vKey: 'hand_size_verified' },
  { label: '40yd Dash', render: a => a.dash_40 ? `${a.dash_40}s` : '—', vKey: 'dash_40_verified' },
  { label: '10yd Split', render: a => a.run_10 ? `${a.run_10}s` : '—', vKey: 'run_10_verified' },
  { label: 'Flying 10', render: a => a.run_20 ? `${a.run_20}s` : '—', vKey: 'run_20_verified' },
  { label: 'Short Shuttle', render: a => a.short_shuttle ? `${a.short_shuttle}s` : '—', vKey: 'short_shuttle_verified' },
  { label: '3-Cone Drill', render: a => a.three_cone_drill ? `${a.three_cone_drill}s` : '—', vKey: 'three_cone_drill_verified' },
  { label: 'Broad Jump', render: a => formatHeight(a.broad_jump), vKey: 'broad_jump_verified' },
  { label: 'Vertical Jump', render: a => a.vertical_jump ? `${a.vertical_jump}"` : '—', vKey: 'vertical_jump_verified' },
  { label: 'Bench Press', render: a => a.bench_press ? `${a.bench_press} lbs` : '—', vKey: 'bench_press_verified' },
  { label: 'Squat', render: a => a.squat ? `${a.squat} lbs` : '—', vKey: 'squat_verified' },
  { label: 'Clean', render: a => a.clean ? `${a.clean} lbs` : '—', vKey: 'clean_verified' },
  { label: 'Deadlift', render: a => a.deadlift ? `${a.deadlift} lbs` : '—', vKey: 'deadlift_verified' },
  { label: 'GPA (W)', render: a => a.gpa_weighted || '—' },
  { label: 'GPA (UW)', render: a => a.gpa_unweighted || '—' },
  { label: 'ACT', render: a => a.act_score || '—' },
  { label: 'SAT', render: a => a.sat_score || '—' },
];

// Which way is "better" for the rows that have a clear winner, and where the raw number lives.
const BEST = {
  'Height': ['height_inches', 'high'], 'Weight': ['weight_lbs', 'high'], 'Wingspan': ['wingspan', 'high'],
  'Hand Size': ['hand_size', 'high'], '40yd Dash': ['dash_40', 'low'], '10yd Split': ['run_10', 'low'],
  'Flying 10': ['run_20', 'low'], 'Short Shuttle': ['short_shuttle', 'low'], '3-Cone Drill': ['three_cone_drill', 'low'],
  'Broad Jump': ['broad_jump', 'high'], 'Vertical Jump': ['vertical_jump', 'high'], 'Bench Press': ['bench_press', 'high'],
  'Squat': ['squat', 'high'], 'Clean': ['clean', 'high'], 'Deadlift': ['deadlift', 'high'],
  'GPA (W)': ['gpa_weighted', 'high'], 'GPA (UW)': ['gpa_unweighted', 'high'], 'ACT': ['act_score', 'high'], 'SAT': ['sat_score', 'high'],
};

// user_ids that hold the best value in a row. Nobody wins when fewer than two athletes have a value or
// when they all tie: highlighting everyone says nothing.
const bestIds = (label, athletes) => {
  const spec = BEST[label];
  if (!spec) return new Set();
  const [key, dir] = spec;
  const vals = athletes.map((a) => [a.user_id, Number(a[key])]).filter(([, v]) => Number.isFinite(v) && v > 0);
  if (vals.length < 2) return new Set();
  const target = dir === 'low' ? Math.min(...vals.map(([, v]) => v)) : Math.max(...vals.map(([, v]) => v));
  const winners = vals.filter(([, v]) => v === target);
  return winners.length === vals.length ? new Set() : new Set(winners.map(([id]) => id));
};

export default function CompareAthletesPage() {
  const { API_URL, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const railView = usesRail(user?.role);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (!ids) { setLoading(false); return; }
    fetchCompare(ids);
  }, [searchParams]);

  const fetchCompare = async (ids) => {
    try {
      const res = await axios.get(`${API_URL}/saved-athletes/compare?ids=${ids}`, getAxiosConfig());
      setAthletes(res.data.athletes || []);
    } catch (e) { console.error('Compare error:', e); }
    setLoading(false);
  };

  // Collect all unique stat names across all athletes
  const allStatNames = new Set();
  athletes.forEach(a => a.game_stats?.forEach(s => allStatNames.add(s.stat_name)));

  const vDot = (v) => v ? <VerifiedFlame style={{ marginLeft: '4px' }} /> : null;

  // ---- Admin / staff / partner: one card, label column + a column per athlete (1j) ----
  if (railView) {
    const removeAthlete = (id) => {
      const left = athletes.filter((a) => a.user_id !== id).map((a) => a.user_id);
      setSearchParams(left.length ? { ids: left.join(',') } : {}, { replace: true });
    };
    const saveAsList = async () => {
      setSaving(true); setSaveError('');
      try {
        const res = await axios.post(`${API_URL}/reports`, {
          title: `Compare · ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          sport: athletes[0]?.sport_slug || 'football',   // football is the app default; the server drops athletes of other sports
          athlete_user_ids: athletes.map((a) => a.user_id),
        }, getAxiosConfig());
        navigate(`/lists?list=${res.data.id}`);
      } catch (e) {
        setSaveError(e.response?.data?.detail || 'Could not save the list.');
        setSaving(false);
      }
    };
    const actions = (
      <div className="cmp-actions">
        <button type="button" className="adm-btn" onClick={() => navigate('/players')}>+ Add athlete</button>
        <button type="button" className="adm-btn adm-btn--dark" disabled={saving || athletes.length < 2} onClick={saveAsList}>Save as list</button>
      </div>
    );
    return (
      <AdminPage active="search" title="Compare" actions={actions}
        subtitle={athletes.length ? `${athletes.length} athletes, side by side. Best value per row in green.` : undefined}
        breadcrumb={[{ label: 'Search', to: '/players' }, { label: 'Compare' }]}>
        {saveError && <div className="ls-banner ls-banner--error" role="alert">{saveError}</div>}
        <div className="adm__card adm__card--flush">
          {loading ? <p className="adm-loading">Loading comparison…</p>
            : athletes.length < 2 ? (
              <div className="adm-empty">
                <p>Need at least 2 athletes to compare.</p>
                <button type="button" className="ls-btn ls-btn--primary" onClick={() => navigate('/players')}>Back to Search</button>
              </div>
            ) : (
              <div className="adm-tablewrap">
                <table className="cmp-table">
                  <thead>
                    <tr>
                      <th className="cmp-label" />
                      {athletes.map((a) => (
                        <th key={a.user_id} className="cmp-head">
                          <button type="button" className="cmp-x" aria-label={`Remove ${a.name}`} onClick={() => removeAthlete(a.user_id)}>×</button>
                          <span className="cmp-avatar" aria-hidden="true">
                            {a.primary_photo ? <img src={a.primary_photo} alt="" className="cmp-avatar__img" />
                              : (a.name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                          </span>
                          <span className="cmp-name">{a.name}</span>
                          <span className="cmp-school">{a.high_school || ''}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td colSpan={athletes.length + 1} className="cmp-section">INFO</td></tr>
                    {INFO_ROWS.map((row) => (
                      <tr key={row.label}>
                        <td className="cmp-label">{row.label}</td>
                        {athletes.map((a) => <td key={a.user_id} className="cmp-cell">{row.render ? row.render(a) : (a[row.key] || '—')}</td>)}
                      </tr>
                    ))}
                    <tr><td colSpan={athletes.length + 1} className="cmp-section">MEASURABLES</td></tr>
                    {MEASURABLE_ROWS.map((row) => {
                      const winners = bestIds(row.label, athletes);
                      return (
                        <tr key={row.label}>
                          <td className="cmp-label">{row.label}</td>
                          {athletes.map((a) => (
                            <td key={a.user_id} className={`cmp-cell ${winners.has(a.user_id) ? 'cmp-best' : ''}`}>
                              {row.render(a)}{row.vKey ? vDot(a[row.vKey]) : null}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {allStatNames.size > 0 && (
                      <>
                        <tr><td colSpan={athletes.length + 1} className="cmp-section">GAME STATS (SENIOR YEAR)</td></tr>
                        {[...allStatNames].sort().map((statName) => (
                          <tr key={statName}>
                            <td className="cmp-label">{statName}</td>
                            {athletes.map((a) => {
                              const stat = a.game_stats?.find((x) => x.stat_name === statName && x.year_offset === 3);
                              return <td key={a.user_id} className="cmp-cell">{stat?.stat_value || '—'}</td>;
                            })}
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </AdminPage>
    );
  }

  if (loading) return <div className="settings-page"><p>Loading comparison...</p></div>;
  if (athletes.length < 2) return (
    <div className="settings-page">
      <p>Need at least 2 athletes to compare.</p>
      <Button variant="primary" onClick={() => navigate('/players')}>Back to Search</Button>
    </div>
  );

  return (
    <div className="ed-page">
      <PageHeader
        eyebrow={
          <>
            <MonoLabel>COMPARE · ATHLETES</MonoLabel>
            <MonoLabel>·</MonoLabel>
            <MonoLabel>{athletes.length} SELECTED</MonoLabel>
          </>
        }
        title="Compare"
        accent="prospects."
        sub="Side-by-side measurables, academics, and stats for the athletes you selected."
        actions={
          <EditorialButton variant="ghost" onClick={() => navigate('/players')}>
            ← Back to Search
          </EditorialButton>
        }
      />

      <div style={{ overflowX: 'auto' }}>
        <table className="compare-table">
          <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff' }}>
            <tr>
              <th style={{ minWidth: '140px' }}></th>
              {athletes.map(a => (
                <th key={a.user_id} style={{ minWidth: '160px', textAlign: 'center' }}>
                  {a.primary_photo && <img src={a.primary_photo} alt="" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', display: 'block', margin: '0 auto 4px' }} />}
                  <div style={{ fontWeight: 600, fontSize: '13px' }}>{a.name}</div>
                  {a.saved_grade && (
                    <div style={{ fontSize: '12px', color: '#f59e0b' }}>
                      {'★'.repeat(a.saved_grade)}{'☆'.repeat(5 - a.saved_grade)}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Info Section */}
            <tr><td colSpan={athletes.length + 1} className="compare-section-header">Info</td></tr>
            {INFO_ROWS.map(row => (
              <tr key={row.label}>
                <td style={{ fontWeight: 500, color: '#475569', padding: '6px 12px', fontSize: '13px' }}>{row.label}</td>
                {athletes.map(a => (
                  <td key={a.user_id} style={{ textAlign: 'center', padding: '6px 12px', fontSize: '13px' }}>
                    {row.render ? row.render(a) : (a[row.key] || '—')}
                  </td>
                ))}
              </tr>
            ))}

            {/* Measurables Section */}
            <tr><td colSpan={athletes.length + 1} className="compare-section-header">Measurables</td></tr>
            {MEASURABLE_ROWS.map(row => (
              <tr key={row.label}>
                <td style={{ fontWeight: 500, color: '#475569', padding: '6px 12px', fontSize: '13px' }}>{row.label}</td>
                {athletes.map(a => (
                  <td key={a.user_id} style={{ textAlign: 'center', padding: '6px 12px', fontSize: '13px' }}>
                    {row.render(a)}{row.vKey ? vDot(a[row.vKey]) : null}
                  </td>
                ))}
              </tr>
            ))}

            {/* Game Stats Section */}
            {allStatNames.size > 0 && (
              <>
                <tr><td colSpan={athletes.length + 1} className="compare-section-header">Game Stats (Senior Year)</td></tr>
                {[...allStatNames].sort().map(statName => (
                  <tr key={statName}>
                    <td style={{ fontWeight: 500, color: '#475569', padding: '6px 12px', fontSize: '13px' }}>{statName}</td>
                    {athletes.map(a => {
                      // Senior year = year_offset 3 (canonical: 0=Freshman … 3=Senior).
                      const stat = a.game_stats?.find(s => s.stat_name === statName && s.year_offset === 3);
                      return (
                        <td key={a.user_id} style={{ textAlign: 'center', padding: '6px 12px', fontSize: '13px' }}>
                          {stat?.stat_value || '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
